import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { MdEmail, MdLockReset, MdLock, MdVpnKey } from 'react-icons/md';
import { authAPI } from '../services/api';

// ─────────────────────────────────────────────────────────────────────────────
// HCEN · Forgot Password (MSG91 OTP · May 2026)
//
// Step 1 (email) → MSG91 sends a 6-digit code to the registered address.
// Step 2 (code + new password) → confirmForgotPassword sets the new password.
// After success → bounce back to /login.
// ─────────────────────────────────────────────────────────────────────────────
export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep]       = useState(1);
  const [email, setEmail]     = useState('');
  const [code, setCode]       = useState('');
  const [pwd, setPwd]         = useState('');
  const [pwd2, setPwd2]       = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [info, setInfo]       = useState('');

  const sendCode = async (e) => {
    e.preventDefault();
    setError(''); setInfo('');
    if (!email) return setError('Enter your registered email.');
    setLoading(true);
    const { error: err } = await authAPI.forgotPassword(email.toLowerCase().trim());
    setLoading(false);
    if (err) return setError(err);
    setInfo(`If an account exists for ${email}, a 6-digit reset code has been sent from no-reply@hcen.in (check inbox, spam, and promotions).`);
    setStep(2);
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    setError(''); setInfo('');
    if (!code)               return setError('Enter the 6-digit code from your email.');
    if (pwd.length < 8)      return setError('Password must be at least 8 characters.');
    if (!/[A-Z]/.test(pwd))  return setError('Password must contain an uppercase letter.');
    if (!/[a-z]/.test(pwd))  return setError('Password must contain a lowercase letter.');
    if (!/[0-9]/.test(pwd))  return setError('Password must contain a number.');
    if (!/[!@#$%^&*?\-_]/.test(pwd)) return setError('Password must contain a symbol (!@#$%^&*?-_)');
    if (pwd !== pwd2)        return setError('Passwords do not match.');
    setLoading(true);
    const { error: err } = await authAPI.confirmForgotPassword(email.toLowerCase().trim(), code, pwd);
    setLoading(false);
    if (err) return setError(err);
    setInfo('Password reset. Redirecting to login…');
    setTimeout(() => navigate('/login'), 1200);
  };

  return (
    <div className="auth-page">
      <div style={{ width:'100%', maxWidth:440 }}>
        <div style={{ textAlign:'center', marginBottom:28 }}>
          <img src="/hcen_logo.png" alt="HCEN" style={{ width:70, height:70, borderRadius:18, objectFit:'cover', marginBottom:14 }} />
          <h1 style={{ fontFamily:'Outfit,sans-serif', fontSize:28, fontWeight:800, color:'var(--text-primary)', marginBottom:4 }}>Reset Your Password</h1>
          <p style={{ color:'var(--text-secondary)', fontSize:13 }}>Step {step} of 2 · {step === 1 ? 'Get reset code' : 'Set new password'}</p>
        </div>

        <div className="glass-card" style={{ padding:32 }}>
          {step === 1 ? (
            <form onSubmit={sendCode}>
              <div className="form-group">
                <label className="form-label" htmlFor="forgot-email">Registered email</label>
                <div className="auth-input-wrap">
                  <MdEmail className="auth-input-icon" aria-hidden />
                  <input id="forgot-email" type="email" className="form-input" placeholder="hospital@hcen.com" value={email}
                    autoComplete="email"
                    onChange={e => setEmail(e.target.value)} required />
                </div>
              </div>

              {info  && <Banner kind="ok"  text={info}  />}
              {error && <Banner kind="err" text={error} />}

              <button type="submit" className="btn btn-primary btn-lg" disabled={loading} style={{ width:'100%', justifyContent:'center' }}>
                {loading ? 'Sending…' : <><MdLockReset /> Send Reset Code</>}
              </button>
            </form>
          ) : (
            <form onSubmit={resetPassword}>
              <div className="form-group">
                <label className="form-label" htmlFor="reset-code">6-digit reset code</label>
                <div className="auth-input-wrap">
                  <MdVpnKey className="auth-input-icon" aria-hidden />
                  <input id="reset-code" type="text" inputMode="numeric" maxLength={6} className="form-input" placeholder="123456" value={code}
                    onChange={e => setCode(e.target.value.replace(/[^0-9]/g, ''))} style={{ letterSpacing:4 }} required />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="reset-password">New password</label>
                <div className="auth-input-wrap">
                  <MdLock className="auth-input-icon" aria-hidden />
                  <input id="reset-password" type="password" className="form-input" placeholder="At least 8 characters" value={pwd}
                    autoComplete="new-password"
                    onChange={e => setPwd(e.target.value)} required />
                </div>
                <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:6, lineHeight:1.5 }}>
                  Must be 8+ chars with uppercase, lowercase, a number, and a symbol.
                </div>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="reset-password-confirm">Confirm new password</label>
                <div className="auth-input-wrap">
                  <MdLock className="auth-input-icon" aria-hidden />
                  <input id="reset-password-confirm" type="password" className="form-input" placeholder="Re-enter the same password" value={pwd2}
                    autoComplete="new-password"
                    onChange={e => setPwd2(e.target.value)} required />
                </div>
              </div>

              {info  && <Banner kind="ok"  text={info}  />}
              {error && <Banner kind="err" text={error} />}

              <button type="submit" className="btn btn-primary btn-lg" disabled={loading} style={{ width:'100%', justifyContent:'center' }}>
                {loading ? 'Resetting…' : 'Reset Password'}
              </button>

              <button type="button" onClick={() => { setStep(1); setCode(''); setPwd(''); setPwd2(''); setError(''); setInfo(''); }}
                style={{ width:'100%', marginTop:12, padding:'10px', background:'transparent', border:'none', color:'var(--text-muted)', fontSize:13, cursor:'pointer' }}>
                ← Resend code / change email
              </button>
            </form>
          )}

          <div style={{ textAlign:'center', marginTop:22, paddingTop:18, borderTop:'1px solid var(--border-color)' }}>
            <Link to="/login" style={{ fontSize:13, color:'#4f8ef7', textDecoration:'none', fontWeight:600 }}>← Back to Login</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function Banner({ kind, text }) {
  const style = kind === 'ok'
    ? { background:'rgba(0,212,170,0.12)', border:'1px solid rgba(0,212,170,0.3)', color:'#00d4aa' }
    : { background:'rgba(239,68,68,0.12)', border:'1px solid rgba(239,68,68,0.3)', color:'#ef4444' };
  return (
    <div style={{ padding:'11px 14px', borderRadius:'var(--radius-md)', fontSize:13, marginBottom:14, ...style }}>
      {text}
    </div>
  );
}
